/**
 * Care-flow component left-rail (Sprint 4.3.4, PROD-653).
 *
 * Renders the 16 folder entries that mirror the canonical Awell
 * source-control layout (vault `03-knowledge-base/external-references/
 * awell/studio/docs/source-control/concepts/components.md`):
 *
 *   actions / api_calls / data_point_definitions / forms / labels /
 *   messages / pathways / questions / rules / settings / stakeholders /
 *   steps / timings / tracks / transitions / triggers
 *
 * Each row carries a count badge driven by the active pathway's
 * in-memory care-flow component bundle. Selecting a folder fires the
 * `onSelectFolder` callback so the parent view can render the matching
 * `<ComponentFolderView>`.
 *
 * HC2: Tailwind classes resolve through Signal DS tokens. No raw hex.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: rendered as `role="navigation"` with a labelled
 * `<ul role="list">` of focusable buttons; the active folder gets
 * `aria-current="true"` so the keyboard user can hear the selection.
 */
import {
  CARE_FLOW_FOLDERS,
  CARE_FLOW_FOLDER_HINTS,
  CARE_FLOW_FOLDER_LABELS,
  isSharedFolder,
  type CareFlowFolder,
} from '../../services/care-flow-components/types';

export interface CareFlowComponentLeftRailProps {
  activeFolder: CareFlowFolder | null;
  countsByFolder: Record<CareFlowFolder, number>;
  onSelectFolder: (folder: CareFlowFolder) => void;
  className?: string;
}

export function CareFlowComponentLeftRail({
  activeFolder,
  countsByFolder,
  onSelectFolder,
  className,
}: CareFlowComponentLeftRailProps) {
  return (
    <nav
      aria-labelledby="care-flow-left-rail-title"
      role="navigation"
      data-testid="care-flow-left-rail"
      className={[
        'flex w-72 flex-col gap-gutter-sm border-r border-surface-border bg-surface-muted p-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div>
        <h3
          id="care-flow-left-rail-title"
          className="text-signal-sm font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Care flow components
        </h3>
        <p className="mt-gutter-sm text-signal-xs text-text-tertiary">
          16 folders. JSON files, one per component. Mirrors the Awell
          source-control layout.
        </p>
      </div>
      <ul
        role="list"
        aria-label="Care flow component folders"
        data-testid="care-flow-left-rail-list"
        className="flex flex-col gap-1"
      >
        {CARE_FLOW_FOLDERS.map((folder) => {
          const count = countsByFolder[folder] ?? 0;
          const isActive = activeFolder === folder;
          const shared = isSharedFolder(folder);
          return (
            <li key={folder}>
              <button
                type="button"
                data-testid={`care-flow-left-rail-folder-${folder}`}
                data-folder={folder}
                data-folder-active={isActive ? 'true' : 'false'}
                data-folder-shared={shared ? 'true' : 'false'}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => onSelectFolder(folder)}
                className={[
                  'flex w-full items-start justify-between gap-gutter-sm rounded-signal-md border px-gutter-md py-gutter-sm text-left text-signal-sm focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
                  isActive
                    ? 'border-signal-primary-500 bg-surface-base text-text-primary'
                    : 'border-surface-border bg-surface-base text-text-primary hover:bg-surface-muted',
                ].join(' ')}
              >
                <span className="flex flex-col">
                  <span className="flex items-center gap-gutter-sm font-semibold">
                    {CARE_FLOW_FOLDER_LABELS[folder]}
                    {shared ? (
                      <span
                        data-testid={`care-flow-left-rail-shared-badge-${folder}`}
                        className="rounded-signal-sm border border-signal-primary-500 bg-surface-base px-1 text-signal-xs font-semibold text-signal-primary-500"
                      >
                        Shared
                      </span>
                    ) : null}
                  </span>
                  <span className="text-signal-xs text-text-tertiary">
                    {CARE_FLOW_FOLDER_HINTS[folder]}
                  </span>
                </span>
                <span
                  data-testid={`care-flow-left-rail-count-${folder}`}
                  className="shrink-0 rounded-signal-sm bg-surface-muted px-gutter-sm text-signal-xs font-semibold text-text-secondary"
                >
                  {count}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
